import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, TrendingDown, Pencil, Trash2, Repeat, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import FixedExpensesDialog from "@/components/FixedExpensesDialog";

const CATEGORIES = ["Carburant", "Produits", "Salaire", "Loyer", "Maintenance", "Autre"];
const PERIODS = [{ k: "today", l: "Aujourd'hui" }, { k: "week", l: "Semaine" }, { k: "month", l: "Mois" }, { k: "all", l: "Tout" }];

export default function Expenses() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank());
  const [showFixed, setShowFixed] = useState(false);

  function blank() { return { label: "", category: "Carburant", amount: 0, date: new Date().toISOString().slice(0, 10), note: "" }; }

  const load = () => { setLoading(true); api.get(`/expenses?period=${period}`).then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [period]);

  const openNew = () => { setEdit(null); setForm(blank()); setShowForm(true); };
  const openEdit = (e) => { setEdit(e); setForm({ ...blank(), ...e }); setShowForm(true); };

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (edit) await api.put(`/expenses/${edit.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Dépense enregistrée"); setShowForm(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Supprimée"); load(); };
  const pay = async (id) => { await api.post(`/expenses/${id}/pay`); toast.success("Dépense marquée payée"); load(); };
  const unpaid = data.unpaid_fixed;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Dépenses</h1><p className="text-slate-500 text-sm mt-1">Suivi des dépenses opérationnelles</p></div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button data-testid="expense-fixed-btn" variant="outline" onClick={() => setShowFixed(true)}><Repeat className="w-4 h-4 mr-2" /> Dépenses fixes</Button>
          <Button data-testid="expense-new-btn" onClick={openNew} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-2" /> Nouvelle dépense</Button>
        </div>
      </div>

      {unpaid?.count > 0 && (
        <div data-testid="expense-unpaid-alert" className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 font-semibold text-amber-800">
            <span className="flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Charges fixes non payées</span>
            <span data-testid="expense-unpaid-total">{unpaid.count} · {money(unpaid.total)}</span>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {unpaid.items.map((e) => (
              <div key={e.id} data-testid={`expense-unpaid-${e.id}`} className="bg-white border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0"><div className="font-medium truncate">{e.label}</div><div className="text-xs text-slate-500">Échéance {fmtDate(e.date)} · {money(e.amount)}</div></div>
                <Button data-testid={`expense-unpaid-pay-${e.id}`} size="sm" onClick={() => pay(e.id)} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">Payée</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-white border rounded-xl p-1">
          {PERIODS.map((p) => (
            <button key={p.k} data-testid={`expense-period-${p.k}`} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${period === p.k ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{p.l}</button>
          ))}
        </div>
        <div className="bg-white border rounded-2xl px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><TrendingDown className="w-5 h-5" /></div>
          <div><div className="text-xs text-slate-500">Total dépenses</div><div data-testid="expense-total" className="text-xl font-bold font-heading">{money(data.total)}</div></div>
        </div>
      </div>

      {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="bg-white border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Libellé</th><th className="text-left px-4 py-3">Enregistrée par</th><th className="text-left px-4 py-3">Catégorie</th><th className="text-left px-4 py-3">Statut</th><th className="text-right px-4 py-3">Montant</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-t">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(e.date)}</td>
                  <td className="px-4 py-3">{e.label}{e.note && <span className="text-xs text-slate-400 block">{e.note}</span>}</td>
                  <td data-testid={`expense-author-${e.id}`} className="px-4 py-3 text-slate-600">{e.created_by_name}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 rounded-full px-2 py-1">{e.category}</span>{e.fixed_id && <span className="ml-1 text-[10px] text-brand-700 bg-brand-50 rounded-full px-1.5 py-0.5">Fixe</span>}</td>
                  <td className="px-4 py-3">
                    {e.paid === false ? (
                      <button data-testid={`expense-pay-${e.id}`} onClick={() => pay(e.id)} className="text-xs rounded-full px-2 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200">À payer · Marquer payée</button>
                    ) : (
                      <span data-testid={`expense-paid-${e.id}`} title={e.paid_by_name ? `Payée par ${e.paid_by_name}` : ""} className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Payée</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{money(e.amount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(e)} className="p-1.5 text-slate-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`expense-del-${e.id}`} onClick={() => del(e.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Aucune dépense</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
      <FixedExpensesDialog open={showFixed} onOpenChange={setShowFixed} categories={CATEGORIES} onChanged={load} />

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Modifier" : "Nouvelle"} dépense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Libellé *</Label><Input data-testid="ef-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="ef-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Montant ($)</Label><Input data-testid="ef-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Note</Label><Textarea value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" rows={2} /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button data-testid="ef-submit" onClick={save} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
